<script setup>

import PrimaryButton from './PrimaryButton.vue'

import { onMounted, ref } from 'vue'

import {
    Dialog,
    DialogPanel,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Popover,
    PopoverButton,
    PopoverGroup,
    PopoverPanel,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue'

import { XMarkIcon } from '@heroicons/vue/24/outline'
import { ChevronDownIcon } from '@heroicons/vue/20/solid'

const props = defineProps(['title', 'content']);


const currentUrl = new URL(window.location.href);
const urlIndustries = JSON.parse(decodeURIComponent(currentUrl.searchParams.get('industrien')));
const urlTypes = JSON.parse(decodeURIComponent(currentUrl.searchParams.get('typen')));
const urlSort = currentUrl.searchParams.get('sortierung');

const industries = [
    { name: 'Agrar, Chemie, Rohstoffe', checked: urlIndustries?.includes('Agrar, Chemie, Rohstoffe') },
    { name: 'IT und Telekommunikation', checked: urlIndustries?.includes('IT und Telekommunikation') },
    { name: 'Sport und Freizeit', checked: urlIndustries?.includes('Sport und Freizeit') },
    { name: 'Tourismus und Gastronomie', checked: urlIndustries?.includes('Tourismus und Gastronomie') },
    { name: 'Bauen und Wohnen', checked: urlIndustries?.includes('Bauen und Wohnen') },
    { name: 'Handel und Handwerk', checked: urlIndustries?.includes('Handel und Handwerk') },
    { name: 'Energie und Umwelt', checked: urlIndustries?.includes('Energie und Umwelt') },
    { name: 'Finanzen und Immobilien', checked: urlIndustries?.includes('Finanzen und Immobilien') },
    { name: 'Medien und Marketing', checked: urlIndustries?.includes('Medien und Marketing') },
    { name: 'Technik und Industrie', checked: urlIndustries?.includes('Technik und Industrie') },
    { name: 'Gesundheit und Soziales', checked: urlIndustries?.includes('Gesundheit und Soziales') },
    { name: 'Verwaltung und Öffentliche Hand', checked: urlIndustries?.includes('Verwaltung und Öffentliche Hand') },
    { name: 'Bildung', checked: urlIndustries?.includes('Bildung') },
    { name: 'Auto und Mobilität', checked: urlIndustries?.includes('Auto und Mobilität') },
    { name: 'Textil', checked: urlIndustries?.includes('Textil') },
]

const types = [
    { name: 'Vollzeit-Job', checked: urlTypes?.includes('Vollzeit-Job') },
    { name: 'Teilzeit-Job', checked: urlTypes?.includes('Teilzeit-Job') },
    { name: 'Mini/Midi-Job', checked: urlTypes?.includes('Mini/Midi-Job') },
    { name: 'Ausbildung', checked: urlTypes?.includes('Ausbildung') },
    { name: 'Duales Studium', checked: urlTypes?.includes('Duales Studium') }
]

const sort = ref(currentUrl.searchParams.get('sortierung') ?? 'auto');

const submit = () => {
    const checkedIndustries = industries.filter(industrie => industrie.checked).map(industrie => industrie.name);
    const checkedTypes = types.filter(type => type.checked).map(type => type.name);

    if (checkedIndustries.length) {
        currentUrl.searchParams.set('industrien', encodeURIComponent(JSON.stringify(checkedIndustries)));
    } else {
        currentUrl.searchParams.delete('industrien');
    }

    if (checkedTypes.length) {
        currentUrl.searchParams.set('typen', encodeURIComponent(JSON.stringify(checkedTypes)));
    } else {
        currentUrl.searchParams.delete('typen');
    }

    if (sort.value == 'neuste') {
        currentUrl.searchParams.set('sortierung', 'neuste');
    } else {
        currentUrl.searchParams.delete('sortierung');
    }

    window.location = currentUrl.href;
}

const reset = () => {
    currentUrl.searchParams.delete('typen');
    currentUrl.searchParams.delete('industrien');
    currentUrl.searchParams.delete('sortierung');
    window.location = currentUrl.href;
}

</script>

<template>
    <PopoverGroup class="sm:tw-items-baseline sm:tw-space-x-8">
        <Popover as="div" class="tw-relative tw-inline-block tw-text-left">
            <div>
                <PopoverButton
                    class="group tw-inline-flex tw-items-center tw-justify-center tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-text-gray-900 tw-bg-transparent tw-outline-none tw-pr-0">

                    <div data-title="Stellenangebote filtern" data-toggle="tooltip" data-placement="top">

                        <div class="btn-group" role="group">
                            <a v-if="urlTypes || urlIndustries || urlSort" class="btn btn-primary max-sm:!tw-hidden"
                                href="#" @click="reset"><i class="fas fa-times"></i></a>

                            <button
                                class="btn btn-secondary dropdown-toggle max-sm:!tw-bg-transparent max-sm:!tw-text-primary-500 max-sm:hover:tw-underline max-sm:tw-p-0 max-sm:!tw-block max-sm:tw-text-left max-sm:!tw-shadow-none"
                                id="sort-dropdown" role="button" data-toggle="dropdown" aria-haspopup="true"
                                aria-expanded="false" data-display="static">
                                <i class="fas fa-sort-alpha-down mr-2"></i>
                                Filtern und Sortieren
                            </button>
                        </div>
                    </div>

                </PopoverButton>
            </div>

            <transition enter-active-class="tw-transition tw-ease-out tw-duration-100"
                enter-from-class="tw-transform tw-opacity-0 tw-scale-95"
                enter-to-class="tw-transform tw-opacity-100 tw-scale-100"
                leave-active-class="tw-transition tw-ease-in tw-duration-75"
                leave-from-class="tw-transform tw-opacity-100 tw-scale-100"
                leave-to-class="tw-transform tw-opacity-0 tw-scale-95">
                <PopoverPanel
                    class="tw-absolute tw-z-20 tw-mt-2 tw-min-w-[20rem] tw-right-0 tw-origin-top-right tw-rounded-md tw-bg-white tw-p-4 tw-shadow-2xl tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none tw-max-h-[73vh] tw-overflow-y-scroll">

                    <form class="tw-space-y-4">
                        <div class="tw-flex tw-flex-col">

                            <form @submit.prevent="submit">
                                <span class="tw-font-medium tw-text-gray-900 tw-mb-3 tw-pb-1 tw-border-b tw-block">
                                    Branchen
                                </span>

                                <div class="tw-space-y-1">
                                    <div v-for="(industry, index) in industries" :key="index"
                                        class="tw-flex tw-items-center">
                                        <input :id="'industrie-' + index" type="checkbox" v-model="industry.checked"
                                            class="tw-h-4 tw-w-4 tw-rounded tw-border-gray-300 tw-text-indigo-600 focus:tw-ring-indigo-500" />
                                        <label :for="'industrie-' + index"
                                            class="tw-ml-3 tw-whitespace-nowrap tw-pr-6 tw-text-gray-700 tw-mb-0">
                                            {{ industry.name }}
                                        </label>
                                    </div>
                                </div>

                                <span
                                    class="tw-mt-4 tw-font-medium tw-text-gray-900 tw-mb-3 tw-pb-1 tw-border-b tw-block">
                                    Typ
                                </span>

                                <div class="tw-space-y-1">
                                    <div v-for="(type, index) in types" :key="index" class="tw-flex tw-items-center">
                                        <input :id="'type-' + index" type="checkbox" v-model="type.checked"
                                            class="tw-h-4 tw-w-4 tw-rounded tw-border-gray-300 tw-text-indigo-600 focus:tw-ring-indigo-500" />
                                        <label :for="'type-' + index"
                                            class="tw-ml-3 tw-whitespace-nowrap tw-pr-6 tw-text-gray-700 tw-mb-0">
                                            {{ type.name }}
                                        </label>
                                    </div>
                                </div>

                                <span
                                    class="tw-mt-4 tw-font-medium tw-text-gray-900 tw-mb-3 tw-pb-1 tw-border-b tw-block">
                                    Sortieren
                                </span>

                                <div class="tw-space-y-1">
                                    <div class="tw-flex tw-items-center">
                                        <input id="sort-auto" type="radio" value="auto" v-model="sort" name="sort"
                                            class="tw-h-4 tw-w-4 tw-rounded tw-border-gray-300 tw-text-indigo-600 focus:tw-ring-indigo-500" />

                                        <label for="sort-auto"
                                            class="tw-ml-3 tw-whitespace-nowrap tw-pr-6 tw-text-gray-700 tw-mb-0">
                                            Nach Relevanz sortieren
                                        </label>
                                    </div>

                                    <div class="tw-flex tw-items-center">
                                        <input id="sort-newest" type="radio" value="neuste" v-model="sort" name="sort"
                                            class="tw-h-4 tw-w-4 tw-rounded tw-border-gray-300 tw-text-indigo-600 focus:tw-ring-indigo-500" />

                                        <label for="sort-newest"
                                            class="tw-ml-3 tw-whitespace-nowrap tw-pr-6 tw-text-gray-700 tw-mb-0">
                                            Aktuellster Eintrag zuerst
                                        </label>
                                    </div>
                                </div>
                                <PrimaryButton class="tw-mt-5">Speichern</PrimaryButton>
                            </form>

                        </div>
                    </form>
                </PopoverPanel>
            </transition>
        </Popover>
    </PopoverGroup>
</template>
